export type ApiErrorBody = {
  code?: string;
  message?: string;
  detail?: string | null;
  requestId?: string;
  retryable?: boolean;
  diagnostics?: Record<string, unknown> | null;
};

export class KinveraApiError extends Error {
  status?: number;
  code?: string;
  detail?: string | null;
  requestId?: string;
  retryable?: boolean;
  diagnostics?: Record<string, unknown> | null;
  payload?: unknown;

  constructor(message: string, init: Partial<KinveraApiError> = {}) {
    super(message);
    this.name = 'KinveraApiError';
    Object.assign(this, init);
  }
}

/** Format an error for the Specialist Consultation chat window. */
export function formatErrorForChat(err: unknown, context: string): string {
  if (err instanceof KinveraApiError) {
    const parts = [`${context} failed.`];
    parts.push(err.message);
    if (err.code) parts.push(`Code: ${err.code}`);
    if (err.detail) parts.push(`Detail: ${err.detail}`);
    if (err.requestId) parts.push(`Request ID: ${err.requestId}`);
    if (err.retryable) parts.push('This error may be retryable — please try again.');
    return parts.join(' ');
  }
  if (err instanceof Error) {
    return `${context} failed. ${err.message}`;
  }
  return `${context} failed. ${String(err)}`;
}

type ApiOptions = RequestInit & { token?: string | null };

type AuthTokenProvider = () => Promise<string | null>;

let authTokenProvider: AuthTokenProvider | null = null;

/** Register a provider that returns a fresh Firebase ID token for authenticated API calls. */
export function setAuthTokenProvider(provider: AuthTokenProvider | null) {
  authTokenProvider = provider;
}

async function resolveAuthToken(explicit?: string | null): Promise<string | null> {
  if (explicit) return explicit;
  if (!authTokenProvider) return null;
  try {
    return await authTokenProvider();
  } catch (err) {
    console.warn('Auth token provider failed:', err);
    return null;
  }
}

function parseStructuredError(data: Record<string, unknown>, status: number, path: string): KinveraApiError {
  const nested = data.error;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const body = nested as ApiErrorBody;
    return new KinveraApiError(body.message || `Request failed (${status})`, {
      status,
      code: body.code,
      detail: body.detail,
      requestId: body.requestId,
      retryable: body.retryable,
      diagnostics: body.diagnostics || null,
      payload: data,
    });
  }
  if (typeof nested === 'string') {
    return new KinveraApiError(nested, {
      status,
      code: typeof data.code === 'string' ? data.code : undefined,
      payload: data,
    });
  }
  if (typeof data.message === 'string') {
    return new KinveraApiError(data.message, { status, payload: data });
  }
  return new KinveraApiError(`Request failed (${status}) for ${path}`, { status, payload: data });
}

async function apiFetch<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }
  const token = await resolveAuthToken(options.token);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (import.meta.env.VITE_USE_DEV_AUTH === 'true') {
    headers.set('X-Dev-User-Id', 'dev-user');
    headers.set('X-Dev-User-Email', 'dev@kinvera.local');
  }

  let res: Response;
  try {
    res = await fetch(path, { ...options, headers });
  } catch (networkErr) {
    throw new KinveraApiError(
      networkErr instanceof Error ? networkErr.message : 'Network request failed',
      { code: 'NETWORK_ERROR', retryable: true }
    );
  }

  const contentType = res.headers.get('content-type') || '';
  const raw = await res.text();

  let data: Record<string, unknown> = {};
  const looksJson = contentType.includes('application/json') || raw.trim().startsWith('{');
  if (looksJson && raw.trim()) {
    try {
      data = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      throw new KinveraApiError(`API returned invalid JSON from ${path}`, {
        status: res.status,
        code: 'INVALID_JSON',
        detail: raw.slice(0, 200),
      });
    }
  } else if (raw.trim().startsWith('<!') || contentType.includes('text/html')) {
    throw new KinveraApiError(
      'Kinvera API is not reachable. Hosting returned a web page instead of JSON — Cloud Run kinvera-api may be down or the /api/** rewrite is missing.',
      { status: res.status, code: 'API_UNREACHABLE', retryable: true }
    );
  }

  if (!res.ok || data.success === false) {
    throw parseStructuredError(data, res.status, path);
  }

  return data as T;
}

export const api = {
  health: () => apiFetch<{ ok: boolean }>('/api/health'),
  me: (token?: string | null) =>
    apiFetch<{
      uid: string;
      email: string | null;
      credits: number;
      freeRestorationsRemaining: number;
      canRestore: boolean;
      stage?: string;
      creditMode?: string;
      creditsEnforced?: boolean;
      restorationsCurrentlyFree?: boolean;
      watermarkExportsEnabled?: boolean;
      billingEnabled?: boolean;
      hasStripeCustomer?: boolean;
      planLabel?: string;
      subscription?: null;
    }>('/api/me', { token }),
  config: () =>
    apiFetch<{
      stage: string;
      creditMode: string;
      creditsEnforced: boolean;
      restorationsCurrentlyFree: boolean;
      watermarkExportsEnabled?: boolean;
      billingEnabled?: boolean;
      subscriptionsEnabled?: boolean;
      planLabel?: string;
      upscaleEnabled?: boolean;
      upscaleCreditCost?: number;
    }>('/api/config'),
  upscaleCapabilities: () =>
    apiFetch<{
      enabled: boolean;
      provider?: string;
      factors: number[];
      maxOutputPixels?: number | null;
      creditCost: number;
      creditsRequired?: boolean;
      creditMode?: string;
      supportedMimeTypes?: string[];
      defaultOutputMimeType?: string;
      model?: string;
      upscaleMode?: string | null;
    }>('/api/upscale/capabilities'),
  upscale: (
    payload: {
      image: { mimeType: string; base64Data: string; fileName?: string };
      factor: number;
      inputWidth: number;
      inputHeight: number;
      outputMimeType?: string;
      fileName?: string;
    },
    token?: string | null
  ) =>
    apiFetch<{
      success: boolean;
      pending?: boolean;
      jobId: string;
      operationId: string;
      status: string;
      jobType?: string;
      message?: string;
      creditDecision?: {
        creditMode: string;
        wouldChargeCredits: number;
        actualCreditsCharged: number;
      };
      diagnostics?: Record<string, unknown> | null;
    }>('/api/upscale', {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    }),
  upscaleJob: (
    jobId: string,
    payload: {
      sourceVersionId?: string;
      sourceVersionLabel?: string;
      image?: { mimeType: string; base64Data: string };
      factor: number;
      inputWidth: number;
      inputHeight: number;
      outputMimeType?: string;
    },
    token?: string | null
  ) =>
    apiFetch<{
      success: boolean;
      pending?: boolean;
      jobId: string;
      operationId: string;
      status: string;
      jobType?: string;
      message?: string;
      creditDecision?: {
        creditMode: string;
        wouldChargeCredits: number;
        actualCreditsCharged: number;
      };
      diagnostics?: Record<string, unknown> | null;
    }>(`/api/jobs/${encodeURIComponent(jobId)}/upscale`, {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    }),
  upscaleOperation: (operationId: string, token?: string | null) =>
    apiFetch<{
      success?: boolean;
      id: string;
      jobId: string;
      status: string;
      factor: number;
      inputWidth: number | null;
      inputHeight: number | null;
      outputWidth: number | null;
      outputHeight: number | null;
      imageUrl: string | null;
      error: { code?: string; message?: string } | null;
      diagnostics?: Record<string, unknown> | null;
    }>(`/api/upscale/operations/${encodeURIComponent(operationId)}`, { token }),
  upscaleSource: (jobId: string, token?: string | null) =>
    apiFetch<{
      jobId: string;
      jobType: string;
      fileName: string;
      sourceKind: string;
      sourceLabel: string;
      width: number;
      height: number;
      mimeType: string;
      base64Data: string;
      dataUrl: string;
      existingUpscales: Array<{
        id: string;
        factor: number;
        inputWidth: number;
        inputHeight: number;
        outputWidth: number;
        outputHeight: number;
        createdAt?: string;
      }>;
    }>(`/api/jobs/${encodeURIComponent(jobId)}/upscale-source`, { token }),
  account: (token?: string | null) =>
    apiFetch<{
      profile: {
        displayName: string | null;
        email: string | null;
        photoURL: string | null;
        provider: string;
      };
      entitlement: Record<string, unknown>;
      billing: {
        billingEnabled: boolean;
        hasStripeCustomer: boolean;
        planLabel: string;
        subscription: null;
      };
      library: { recentJobCount: number };
    }>('/api/account', { token }),
  creditHistory: (token?: string | null, limit = 40) =>
    apiFetch<{ entries: Array<Record<string, unknown>>; warning?: string }>(
      `/api/account/credits/history?limit=${limit}`,
      { token }
    ),
  credits: (token?: string | null) =>
    apiFetch<{
      credits: number;
      freeRestorationsRemaining: number;
      canRestore: boolean;
      stage?: string;
      creditMode?: string;
      creditsEnforced?: boolean;
      restorationsCurrentlyFree?: boolean;
    }>('/api/credits', { token }),
  packs: () =>
    apiFetch<{
      packs: Array<{
        id: string;
        credits: number;
        label: string;
        suggestedUsd: number;
        purchasable?: boolean;
        stripeConfigured?: boolean;
      }>;
      billingEnabled?: boolean;
    }>('/api/pricing/packs'),
  checkout: (packId: string, token?: string | null) =>
    apiFetch<{ url: string; mock?: boolean; balance?: unknown }>('/api/billing/checkout', {
      method: 'POST',
      token,
      body: JSON.stringify({ packId }),
    }),
  billingPortal: (token?: string | null, returnUrl?: string) =>
    apiFetch<{ url: string }>('/api/billing/portal', {
      method: 'POST',
      token,
      body: JSON.stringify({ returnUrl }),
    }),
  analyze: (
    payload: {
      original: { base64Data: string; mimeType: string; fileName?: string };
      preview: { base64Data: string; mimeType: string; fileName?: string };
      subjectMap?: { base64Data: string; mimeType: string; fileName?: string };
      detectedFaces?: Array<{
        index: number;
        faceBounds: { xMin: number; yMin: number; xMax: number; yMax: number };
        confidence?: number;
      }>;
      /** Non-sensitive EXIF/capture hints (never GPS/location). */
      captureHints?: {
        dateTimeOriginal?: string | null;
        cameraMake?: string | null;
        cameraModel?: string | null;
        software?: string | null;
        orientation?: number | null;
        pixelWidth?: number | null;
        pixelHeight?: number | null;
      } | null;
      fileName?: string;
    },
    token?: string | null
  ) =>
    apiFetch<{ jobId: string; analysis: unknown; warning?: string | null }>('/api/analyze', {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    }),
  restore: (payload: Record<string, unknown>, token?: string | null) =>
    apiFetch<{
      jobId: string;
      imageUrl: string;
      commentary: string;
      debit?: unknown;
      warning?: string | null;
    }>('/api/restore', {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    }),
  jobs: (token?: string | null) =>
    apiFetch<{
      jobs: Array<{
        id: string;
        fileName?: string;
        jobType?: string;
        status?: string;
        statusLabel?: string;
        versionLabels?: string[];
        versionCount?: number;
        displayAssetId?: string | null;
        displayVersionLabel?: string | null;
        createdAt?: string;
        updatedAt?: string;
        thumbnailUrl?: string | null;
        hasRestoration?: boolean;
        hasUpscale?: boolean;
        hasPrintReady?: boolean;
        upscaleFactors?: number[];
        downloadUrl?: string | null;
        canUpscale?: boolean;
      }>;
    }>('/api/jobs', { token }),
  photoDetail: (jobId: string, token?: string | null) =>
    apiFetch<{
      success?: boolean;
      jobId: string;
      fileName: string;
      jobType: string;
      status: string | null;
      createdAt: string | null;
      updatedAt: string | null;
      displayAssetId: string | null;
      versions: Array<{
        id: string;
        type: string;
        label: string;
        imageUrl: string | null;
        width: number | null;
        height: number | null;
        megapixels: number | null;
        createdAt: string | null;
        isCurrent: boolean;
        factor: number | null;
        purpose: string | null;
        targetPrintSize: string | null;
        effectivePpi: number | null;
        requiresCrop: boolean | null;
        provider: string | null;
        model: string | null;
        upscaleRequired: boolean | null;
      }>;
      currentVersion: Record<string, unknown> | null;
      printPlans: Array<Record<string, unknown>>;
      versionSummary: {
        count: number;
        labels: string[];
        hasRestored: boolean;
        hasUpscale: boolean;
        hasPrintReady: boolean;
      };
      downloadFileNameHint?: string;
    }>(`/api/jobs/${encodeURIComponent(jobId)}/photo-detail`, { token }),
  printPlan: (
    jobId: string,
    payload: { sourceAssetId?: string | null; targetPrintSize: string },
    token?: string | null
  ) =>
    apiFetch<{ success: boolean; plan: Record<string, unknown> }>(
      `/api/jobs/${encodeURIComponent(jobId)}/print-plan`,
      { method: 'POST', token, body: JSON.stringify(payload) }
    ),
  preparePrint: (
    jobId: string,
    payload: { sourceAssetId?: string | null; targetPrintSize: string },
    token?: string | null
  ) =>
    apiFetch<{
      success: boolean;
      pending?: boolean;
      prepared?: boolean;
      jobId: string;
      operationId?: string;
      status?: string;
      versionId?: string;
      displayAssetId?: string;
      plan?: Record<string, unknown>;
      message?: string;
      diagnostics?: Record<string, unknown> | null;
    }>(`/api/jobs/${encodeURIComponent(jobId)}/prepare-print`, {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    }),
  setDisplayAsset: (jobId: string, displayAssetId: string, token?: string | null) =>
    apiFetch<{ success: boolean; displayAssetId: string; jobId: string }>(
      `/api/jobs/${encodeURIComponent(jobId)}/display-asset`,
      { method: 'POST', token, body: JSON.stringify({ displayAssetId }) }
    ),
  printConfig: () =>
    apiFetch<{
      targetPpi: number;
      printSizes: Array<{ id: string; label: string; widthIn: number; heightIn: number }>;
      upscaleFactors: number[];
      providers: Array<{
        provider: string;
        displayName: string;
        orderingEnabled: boolean;
        status: string;
        message: string;
        supportedPrintSizes: string[];
      }>;
    }>('/api/print/config'),
  deleteJob: (id: string, token?: string | null) =>
    apiFetch<{ ok: boolean }>(`/api/jobs/${id}`, { method: 'DELETE', token }),
  googlePhotosAuthUrl: (token?: string | null) =>
    apiFetch<{ url: string }>('/api/integrations/google-photos/auth-url', { token }),
  googlePhotosCallback: (code: string, token?: string | null) =>
    apiFetch<{ ok: boolean; connected: boolean; expiresAt?: number; connectedAt?: string }>(
      '/api/integrations/google-photos/callback',
      { method: 'POST', token, body: JSON.stringify({ code }) }
    ),
  googlePhotosStatus: (token?: string | null) =>
    apiFetch<{
      connected: boolean;
      configured?: boolean;
      message?: string;
      connectedAt?: string | null;
      expiresAt?: number | null;
      scopes?: string[];
    }>('/api/integrations/google-photos/status', { token }),
  googlePhotosDisconnect: (token?: string | null) =>
    apiFetch<{ connected: boolean }>('/api/integrations/google-photos/disconnect', {
      method: 'POST',
      token,
    }),
  googlePhotosPickerCreate: (payload?: { maxItemCount?: number }, token?: string | null) =>
    apiFetch<{
      mock?: boolean;
      message?: string;
      sessionId?: string;
      pickerUri?: string | null;
      pollingConfig?: { pollIntervalMs: number; timeoutInMs: number };
      mediaItemsSet?: boolean;
    }>('/api/integrations/google-photos/picker/sessions', {
      method: 'POST',
      token,
      body: JSON.stringify(payload || {}),
    }),
  googlePhotosPickerSession: (sessionId: string, token?: string | null) =>
    apiFetch<{
      sessionId: string;
      mediaItemsSet: boolean;
      pollingConfig?: { pollIntervalMs: number; timeoutInMs: number };
    }>(`/api/integrations/google-photos/picker/sessions/${encodeURIComponent(sessionId)}`, {
      token,
    }),
  googlePhotosPickerImport: (sessionId: string, token?: string | null) =>
    apiFetch<{
      images: Array<{ fileName: string; mimeType: string; base64Data: string; dataUrl: string }>;
      count: number;
    }>(`/api/integrations/google-photos/picker/sessions/${encodeURIComponent(sessionId)}/import`, {
      method: 'POST',
      token,
    }),
  googlePhotosPickerDelete: (sessionId: string, token?: string | null) =>
    apiFetch<{ ok: boolean }>(
      `/api/integrations/google-photos/picker/sessions/${encodeURIComponent(sessionId)}`,
      { method: 'DELETE', token }
    ),
  googlePhotosSave: (
    payload: { mimeType: string; base64Data: string; fileName?: string; description?: string },
    token?: string | null
  ) =>
    apiFetch<{ ok: boolean }>('/api/integrations/google-photos/save', {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    }),
};
